import { supabase } from '@/integrations/supabase/client';
import { badgesService } from '@/services/badgesService';

/**
 * Script de migração para conceder badges retroativamente a usuários existentes
 * 
 * Execute este script uma única vez para processar todos os usuários que já
 * completaram ações que merecem badges mas não os receberam porque o sistema
 * de badges não estava persistindo no banco de dados.
 * 
 * COMO EXECUTAR:
 * 1. Faça login como admin
 * 2. Abra o console do navegador (F12)
 * 3. Execute: await window.migrateBadges()
 * 4. Aguarde a conclusão (pode levar alguns minutos)
 */

export async function migrateBadgesForExistingUsers() {
  console.log('🚀 Iniciando migração de badges para usuários existentes...');
  
  try {
    // Buscar todos os usuários com onboarding completo
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, nome')
      .eq('onboarding_completed', true);

    if (profilesError) {
      console.error('❌ Erro ao buscar perfis:', profilesError);
      return;
    }

    if (!profiles || profiles.length === 0) {
      console.log('ℹ️ Nenhum usuário encontrado para migração');
      return;
    }

    console.log(`📊 Processando ${profiles.length} usuários...`);

    let successCount = 0;
    let errorCount = 0;

    // Processar cada usuário
    for (const profile of profiles) {
      try {
        console.log(`\n👤 Processando ${profile.nome} (${profile.id})...`);
        
        // Verificar e conceder badges para este usuário
        await badgesService.checkAndAwardBadges(profile.id);
        
        successCount++;
        console.log(`✅ ${profile.nome} processado com sucesso`);
      } catch (error) {
        errorCount++;
        console.error(`❌ Erro ao processar ${profile.nome}:`, error);
      }
    }

    console.log('\n🎉 Migração concluída!');
    console.log(`✅ Sucessos: ${successCount}`);
    console.log(`❌ Erros: ${errorCount}`);
    console.log(`📊 Total processado: ${profiles.length}`);

    // Buscar estatísticas de badges concedidos
    const { data: badgeStats } = await supabase
      .from('user_badges')
      .select('badge_id, user_id')
      .order('badge_id');

    if (badgeStats) {
      const badgeCount = badgeStats.length;
      const uniqueBadges = new Set(badgeStats.map(b => b.badge_id)).size;
      const usersWithBadges = new Set(badgeStats.map(b => b.user_id)).size;
      
      console.log('\n📈 Estatísticas de badges:');
      console.log(`🏆 Total de badges concedidos: ${badgeCount}`);
      console.log(`🎯 Tipos de badges diferentes: ${uniqueBadges}`);
      console.log(`👥 Usuários com badges: ${usersWithBadges}`);
    }

    return {
      success: true,
      processed: profiles.length,
      succeeded: successCount,
      failed: errorCount,
    };
  } catch (error) {
    console.error('💥 Erro fatal durante migração:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido',
    };
  }
}

// Expor função globalmente para fácil execução no console
if (typeof window !== 'undefined') {
  (window as any).migrateBadges = migrateBadgesForExistingUsers;
  console.log('✅ Script de migração carregado! Execute: await window.migrateBadges()');
}
